import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@/v2/components/common/Typography'
import Form from '@/v2/modals/edit-patient-contact-info/Form'
import validator from '@/v2/modals/edit-patient-contact-info/validator'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useUpdatePatient } from '@/v2/hooks/patient/usePatient'
import { toSnakeCase, toCamelCase, formatPhone } from '@/v2/utils/format'
import { Container, Header, Content, Footer } from './styles'

const MobileEditPatientContactInfo = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { onOpenSnackbar } = useSnackbarContext()

  const { patient, onSuccess } = location.state || {}

  const [values, setValues] = useState({
    phoneNumber: formatPhone(patient?.phoneNumber || ''),
    email: patient?.email || '',
    address1: patient?.address1 || '',
    address2: patient?.address2 || '',
    city: patient?.city || '',
    state: patient?.state || '',
    country: patient?.country || '',
    zipcode: patient?.zipcode || '',
  })
  const [error, setError] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const { updatePatient } = useUpdatePatient(patient?.id)

  const handleBack = () => {
    navigate(-1)
  }

  const handleChange = fields => setValues({ ...values, ...fields })

  const handleSubmit = async () => {
    const validErrors = validator(values)
    setError(validErrors || {})

    if (!validErrors) {
      setIsSaving(true)
      try {
        const { error: updateError, ...data } = await updatePatient({
          patient: toSnakeCase({
            id: patient.id,
            ...values,
          }),
        })

        if (updateError) {
          setError(toCamelCase(updateError))
          onOpenSnackbar('Something went wrong.', 'error')
        } else {
          if (onSuccess) {
            onSuccess(data)
          }
          onOpenSnackbar('Patient has been updated.', 'success')
          navigate(-1)
        }
      } catch (e) {
        console.error('handleSubmit error - edit patient contact info', e)
        onOpenSnackbar('Something went wrong.', 'error')
      } finally {
        setIsSaving(false)
      }
    }
  }

  if (!patient) {
    return (
      <Container>
        <Header>
          <Typography variant="h2" fontSize="16px" fontWeight="500">
            Edit Contact Info
          </Typography>
          <IconButton onClick={() => navigate(-1)} size="small">
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <Typography textAlign="center">
            Unable to load patient details.
          </Typography>
        </Content>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Typography variant="h2" fontSize="16px" fontWeight="500">
          Edit Contact Info
        </Typography>
        <IconButton onClick={handleBack} size="small">
          <CloseIcon />
        </IconButton>
      </Header>

      <Content>
        <Form values={values} error={error} onChange={handleChange} />
      </Content>

      <Footer>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          fullWidth
        >
          {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </Footer>
    </Container>
  )
}

export default MobileEditPatientContactInfo
